import React from 'react'

const Technology: React.FC = () => {
  const features = [
    {
      title: 'Quantum-Resistant Security',
      description: 'Our quantum-resistant encryption protocols ensure your assets remain secure even against future quantum computing threats.',
      icon: (
        <svg className="w-6 h-6 text-brand-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
        </svg>
      ),
      points: [
        'Post-quantum cryptography',
        'Zero-knowledge proofs',
        'Multi-signature authentication'
      ],
      color: 'blue'
    },
    {
      title: 'AI-Powered Recovery',
      description: 'Advanced machine learning algorithms analyze blockchain patterns to predict and execute recovery paths with unprecedented accuracy.',
      icon: (
        <svg className="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      points: [
        'Neural network analysis',
        'Pattern recognition',
        'Real-time monitoring'
      ],
      color: 'purple'
    }
  ]

  return (
    <section className="py-20 bg-brand-dark relative overflow-hidden matrix-bg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Quantum-Secure <span className="gradient-text">Technology</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Our proprietary quantum-resistant algorithms and AI systems work in harmony to protect your digital assets
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 items-center">
          {features.map((feature, index) => (
            <div 
              key={feature.title}
              className="relative group"
              style={{ animationDelay: `${index * 200}ms` }}
            >
              <div className="tech-border rounded-2xl p-8 glass-card group-hover:scale-105 transition-all duration-300">
                <div className="flex items-center space-x-4 mb-6">
                  <div className={`w-12 h-12 ${
                    feature.color === 'blue' ? 'bg-blue-500/20' : 'bg-purple-500/20'
                  } rounded-xl flex items-center justify-center`}>
                    {feature.icon}
                  </div>
                  <h3 className="text-2xl font-bold">{feature.title}</h3>
                </div>
                
                <p className="text-gray-300 mb-6">
                  {feature.description}
                </p>
                
                <div className="space-y-4">
                  {feature.points.map((point, pointIndex) => (
                    <div key={pointIndex} className="flex items-center space-x-3">
                      <div className={`w-2 h-2 ${
                        feature.color === 'blue' ? 'bg-brand-blue' : 'bg-purple-400'
                      } rounded-full`}></div>
                      <span className="text-gray-300">{point}</span>
                    </div>
                  ))}
                </div>

                {/* Animated background effect */}
                <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                  <div className={`absolute inset-0 bg-gradient-to-r ${
                    feature.color === 'blue' 
                      ? 'from-blue-500/10 to-cyan-500/10' 
                      : 'from-purple-500/10 to-pink-500/10'
                  } rounded-2xl`}></div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Tech Stats */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { label: 'Security Level', value: '256-bit', suffix: 'Encryption' },
            { label: 'Recovery Rate', value: '99.7%', suffix: 'Success' },
            { label: 'Response Time', value: '<1ms', suffix: 'Average' },
            { label: 'Uptime', value: '99.99%', suffix: 'Guaranteed' }
          ].map((stat, index) => (
            <div 
              key={stat.label}
              className="text-center glass-card rounded-xl p-6 hover:scale-105 transition-all duration-300"
              style={{ animationDelay: `${(index + 2) * 100}ms` }}
            >
              <div className="text-2xl font-bold gradient-text mb-1">{stat.value}</div>
              <div className="text-sm text-gray-400">{stat.suffix}</div>
              <div className="text-xs text-gray-500 mt-1">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

export default Technology
